import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import { Drawer } from 'rsuite';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { PageHeader, Pagination } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import RowPaginator from '../../components/RowPaginator/RowPaginator';

import useComponentState from '../../utils/useComponentState';
import { getAllAuditLogs, getAuditLogsByEntity } from '../../api/auditLogs';
import { formatAuditValue } from '../../utils/dataFormatter';

import styles from './AuditLogs.module.scss';

const AuditLogs = (props) => {

  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['ID', 'Entity', 'Entity ID', 'Action Type', 'Feild Name', 'Old Value', 'New Value'],
    },
    auditLogData: [],
    auditLogPages: 0,
    curPage: 0,
    rowSize: 10,
    slicedAuditLogData: [],
    auditLogLoadingError: '',
    loading: false,
    filterLabel: 'All',
    filterEntityType: 'all',
    filterEntityId: '',
    isClearable: false,
    filterMode: false,
    showAuditLogDrawer: false,
    drawerData: null,
  });
  const {
    tableHeading,
    auditLogData,
    curPage,
    auditLogLoadingError,
    loading,
    auditLogPages,
    rowSize,
    slicedAuditLogData,
    showAuditLogDrawer,
    drawerData,
    filterLabel,
    filterEntityType,
    filterEntityId,
    isClearable,
    filterMode,
  } = state;

  useEffect(() => {
    if (filterMode) {
      searchAuditLogs(filterEntityType, filterEntityId, 0);
    } else {
      getAuditLogDetails();
    }
  }, [rowSize]);

  const getAuditLogDetails = async (page = 0) => {
    setState({ loading: true, auditLogLoadingError: '' });
    try {
      const response = await getAllAuditLogs(props.auth.auth.token, page, Number(rowSize));
      setState({
        auditLogData: response.logs || [],
        slicedAuditLogData: response.logs || [],
        auditLogPages: response.totalPages || 0,
        curPage: response.pageNo || 0,
        filterMode: false,
      });
    } catch (error) {
      setState({
        auditLogLoadingError: 'Oops, something went wrong. Please try again',
      });
    }
    setState({ loading: false });
  };

  const searchAuditLogs = async (entityType, entityId, page = 0) => {
    setState({ loading: true, auditLogLoadingError: '' });
    try {
        const response = await getAuditLogsByEntity(
          props.auth.auth.token,
          entityType,
          entityId,
          page,
          Number(rowSize)
        );
      const flatItems = (response.history || []).map((item) => ({
          ...item,
          entityType: item.entityType || response.entityType,
          entityId: item.entityId || response.entityId,
        }));
      setState({
        auditLogData: flatItems,
        slicedAuditLogData: flatItems,
        auditLogPages: response.totalPages,
        curPage: response.pageNo,
        filterMode: true,
        isClearable: true,
      });
    } catch (error) {
      setState({
        auditLogLoadingError: 'Oops, something went wrong. Please try again',
      });
    }
    setState({ loading: false });
  };

  const loadByEntityType = (entityType) => {
    if (entityType === 'all') {
      getAuditLogDetails();
    } else {
      searchAuditLogs(entityType, '', 0);
    }
  };

  const handleFilterSelect = (label, entityType) => {
    setState({
      filterLabel: label,
      filterEntityType: entityType,
      filterEntityId: '',
      isClearable: false,
    });
    loadByEntityType(entityType);
  };

  const handleClearSearch = () => {
    setState({ filterEntityId: '', isClearable: false });
    loadByEntityType(filterEntityType);
  };

  const handleSearch = () => {
    if (isClearable && filterEntityId) {
      handleClearSearch();
    } else if (filterEntityId) {
      searchAuditLogs(filterEntityType, filterEntityId, 0);
    }
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;
    setState({ showAuditLogDrawer: true, drawerData: rowData });
  };

  const handleDrawerClose = () => {
    setState({ showAuditLogDrawer: false, drawerData: null });
  };

  const handleRowPaginationChange = (event) => {
    const newRowSize = Number(event.target.value);
    setState({ rowSize: newRowSize, curPage: 0 });
  };

  const handlePagesPagination = (page) => {
    if (filterMode) {
      searchAuditLogs(filterEntityType, filterEntityId, page);
    } else {
      getAuditLogDetails(page);
    }
  };

  const renderSearch = () => (
    <InputGroup className='mb-3'>
      <DropdownButton
        as={InputGroup.Prepend}
        variant='secondary'
        title={filterLabel}
        id='audit-search-dropdown'
      >
        <Dropdown.Item onClick={() => handleFilterSelect('All', 'all')}>
          All
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleFilterSelect('User', 'User')}>
          User
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleFilterSelect('Voucher', 'Voucher')}>
          Voucher
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleFilterSelect('Admin', 'AdminAuth')}>
          Admin
        </Dropdown.Item>
      </DropdownButton>

      <Form.Control
        placeholder='Search Entity ID...'
        value={filterEntityId}
        onChange={(e) =>
          setState({ filterEntityId: e.target.value, isClearable: false })
        }
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        maxLength={50}
      />

      <InputGroup.Append>
        <Button variant='secondary' onClick={handleSearch}>
          <FontAwesomeIcon icon={isClearable && filterEntityId ? faTimes : faSearch} />
        </Button>
      </InputGroup.Append>
    </InputGroup>
  );

  const renderDrawer = () => {
    const data = drawerData;
    if (!data) return null;

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>Audit Log Details</Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            <div className={styles.detail_row}>
              <strong>ID:</strong>
              <span>{data.id}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Entity:</strong>
              <span>{data.entityType}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Entity ID:</strong>
              <span>{data.entityId ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Action Type:</strong>
              <span>{data.action}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Field Name:</strong>
              <span>{data.fieldName}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Old Value:</strong>
              <span>{formatAuditValue(data.oldValue) ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>New Value:</strong>
              <span>{formatAuditValue(data.newValue) ?? 'N/A'}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Changed By:</strong>
              <span>{data.changedBy}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Changed At:</strong>
              <span>{data.changedAt}</span>
            </div>
          </div>
        </Drawer.Body>
      </>
    );
  };

  return (
    <div className={styles.root_div}>
      <div>
        <PageHeader text='Audit Logs' />
        <div className={styles.search_area_block}>
          <div className={styles.user_input}>
            <RowPaginator
              handleRowPaginationChange={handleRowPaginationChange}
              rowSize={rowSize}
            />
            <div className={styles.right}>
              {renderSearch()}
            </div>
          </div>
        </div>
        <div>
          <DataTable
            tableData={slicedAuditLogData}
            tableheading={tableHeading}
            handleOnClick={handleDrawerOpen}
            tag='audit-logs'
            fetchError={auditLogLoadingError}
            notFoundMessage={auditLogLoadingError}
            search_filter_loading={loading}
          />
          {!auditLogLoadingError && (
            <div className={styles.dualPaginateContainer}>
              <div className={styles.pages}>
                <span>
                  Page {curPage + 1} of {auditLogPages}
                </span>
                &nbsp;&nbsp;&nbsp;
                <Pagination
                  pageCount={auditLogPages}
                  onChange={handlePagesPagination}
                  currentPage={0}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Drawer
        show={showAuditLogDrawer}
        onHide={handleDrawerClose}
        placement='right'
      >
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(AuditLogs);
